/**
 * WB3-L2 — Action surface projections (single kernel source → multiple UI surfaces).
 *
 * Context menu uses placement-ignore policy: all non-hidden actions on the input surface
 * appear in `contextMenu`, regardless of primary/secondary/more placement.
 *
 * @see doc/system-completion-plan/01-target/BUSINESS_WORKBENCH_NORTH_STAR_V3.md §5 · §6
 */

import type { WorkbenchActionCapabilityState } from './capability-preview';
import { resolveAiSuggestPilotActionIdsForDomain } from './ai-suggest-pilot-registry';
import type {
  WorkbenchAction,
  WorkbenchActionAvailability,
  WorkbenchActionProjections,
  WorkbenchActionSurface,
  WorkbenchActionV3,
} from './action-types';

export type ProjectableWorkbenchAction = WorkbenchAction & WorkbenchActionCapabilityState;

export interface DisabledActionReasonReport {
  ok: boolean;
  violations: readonly string[];
}

const SHORTCUT_PRIORITY_THRESHOLD = 80;

function resolveAvailability(action: ProjectableWorkbenchAction): WorkbenchActionAvailability {
  if (action.availability) {
    return action.availability;
  }
  if (action.forbidden) {
    return 'disabled';
  }
  return 'enabled';
}

/** Resolver SSOT — capability overlay `reason` then legacy `tooltip`. */
export function resolveWorkbenchForbiddenReason(
  action: ProjectableWorkbenchAction
): string | undefined {
  const explicit = action.reason?.trim();
  if (explicit) {
    return explicit;
  }
  const legacy = action.tooltip?.trim();
  if (legacy) {
    return legacy;
  }
  return undefined;
}

function resolveDenialReason(action: ProjectableWorkbenchAction): string | undefined {
  return resolveWorkbenchForbiddenReason(action);
}

/** Maps legacy capability overlay + optional v3 fields onto a v3 action row. */
export function enrichWorkbenchActionV3(action: ProjectableWorkbenchAction): WorkbenchActionV3 {
  const availability = resolveAvailability(action);
  const reason = availability === 'disabled' ? resolveDenialReason(action) : action.reason?.trim();

  return {
    ...action,
    availability,
    ...(reason !== undefined ? { reason } : {}),
  };
}

/** WB3-L3 governance — disabled actions must carry a non-empty reason string. */
export function assertDisabledActionsHaveReason(
  actions: readonly WorkbenchActionV3[]
): DisabledActionReasonReport {
  const violations: string[] = [];

  for (const action of actions) {
    if (action.availability !== 'disabled') {
      continue;
    }
    if (!action.reason?.trim()) {
      violations.push(action.id);
    }
  }

  return { ok: violations.length === 0, violations };
}

function isCommandPaletteCandidate(action: WorkbenchActionV3): boolean {
  return (
    action.placement === 'command-only' ||
    action.surfaces.includes('command') ||
    action.placement === 'more'
  );
}

function isBulkCandidate(action: WorkbenchActionV3): boolean {
  return action.group === 'state_change' || action.surfaces.includes('board');
}

function isShortcutCandidate(action: WorkbenchActionV3): boolean {
  return (
    action.surfaces.includes('shortcut') &&
    action.placement === 'primary' &&
    action.priority >= SHORTCUT_PRIORITY_THRESHOLD
  );
}

/** Marks primary high-priority actions for shortcut surface projection. */
export function augmentWorkbenchShortcutSurfaces<T extends ProjectableWorkbenchAction>(
  actions: readonly T[]
): T[] {
  return actions.map((action) => {
    if (action.placement !== 'primary' || action.priority < SHORTCUT_PRIORITY_THRESHOLD) {
      return action;
    }
    if (action.surfaces.includes('shortcut')) {
      return action;
    }
    return {
      ...action,
      surfaces: [...action.surfaces, 'shortcut'],
    };
  });
}

/** Promotes parity-enrolled AI pilot actions into the aiSuggested projection bucket. */
export function augmentWorkbenchAiSuggestedSurfaces<T extends ProjectableWorkbenchAction>(
  actions: readonly T[],
  businessDomain: string
): T[] {
  const pilotIds = new Set(resolveAiSuggestPilotActionIdsForDomain(businessDomain));
  if (pilotIds.size === 0) {
    return [...actions];
  }
  return actions.map((action) =>
    pilotIds.has(action.id) && action.group !== 'ai_suggested'
      ? { ...action, group: 'ai_suggested' as const }
      : action
  );
}

function filterByInputSurface(
  actions: readonly ProjectableWorkbenchAction[],
  surface: WorkbenchActionSurface | undefined
): ProjectableWorkbenchAction[] {
  if (!surface) {
    return [...actions];
  }
  return actions.filter((action) => action.surfaces.includes(surface));
}

/**
 * Projects a resolved action set onto official Workbench surfaces.
 * IDs are stable across all projection buckets (subset relationship only).
 */
export function projectActions(
  actions: readonly ProjectableWorkbenchAction[],
  surface?: WorkbenchActionSurface
): WorkbenchActionProjections {
  const enriched = filterByInputSurface(actions, surface)
    .map(enrichWorkbenchActionV3)
    .filter((action) => action.availability !== 'hidden');

  return {
    primary: enriched.filter((action) => action.placement === 'primary'),
    secondary: enriched.filter((action) => action.placement === 'secondary'),
    inspector: enriched.filter((action) => action.surfaces.includes('inspector')),
    contextMenu: enriched,
    commandPalette: enriched.filter(isCommandPaletteCandidate),
    bulk: enriched.filter(isBulkCandidate),
    aiSuggested: enriched.filter((action) => action.group === 'ai_suggested'),
    shortcuts: enriched.filter(isShortcutCandidate),
  };
}
